import { useState, useEffect } from "react";
import { 
    Network, Clock, Search, Activity, CheckCircle2, 
    XCircle, ShieldAlert, Hash
} from "lucide-react";
import type { InterfaceEntry } from "./NvrPortVisualization";


interface NvrInterfacesProps {
    initialData?: InterfaceEntry[];
    initialLoading?: boolean;
}

export default function NvrInterfaces({ initialData = [], initialLoading = false }: NvrInterfacesProps) {
    const [data, setData] = useState<InterfaceEntry[]>(initialData);
    const [loading, setLoading] = useState(initialLoading);
    const [totalRecords, setTotalRecords] = useState(initialData.length);

    useEffect(() => {
        setData(initialData);
        setLoading(initialLoading);
        setTotalRecords(initialData.length);
    }, [initialData, initialLoading]);


    return (
        <div className="w-full flex flex-col gap-4 text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Network size={16} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white tracking-wide leading-tight">Network Interfaces</h2>
                        <p className="text-[9px] text-gray-400 uppercase tracking-widest mt-0.5">Port Configuration & Status</p>
                    </div>
                </div>
                
                <div className="text-[9px] font-bold text-gray-400 bg-gray-800/40 px-2 py-1 rounded-md border border-gray-700">
                    Total Interfaces: <span className="text-white">{totalRecords}</span>
                </div>
            </div>



            {/* Details Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-lg overflow-hidden shadow-sm h-fit w-full mt-2">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-800/80 border-b border-gray-700/50">
                            <tr>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap w-16">
                                    <div className="flex items-center gap-1.5">
                                        <Hash size={12} className="text-gray-500" /> 
                                        IDX
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Network size={12} className="text-indigo-400" /> 
                                        Interface Name
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                    MAC Address
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                    Admin / Oper Status
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        <Activity size={12} className="text-amber-400" /> 
                                        Speed
                                    </div>
                                </th>
                                <th className="px-3 py-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                        <Clock size={12} className="text-emerald-400" /> 
                                        Last Updated
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em]">Loading Interfaces...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-8 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <Search size={20} className="text-gray-400" />
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">No interfaces found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-gray-800/40 transition-colors group">
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono font-bold text-gray-500 bg-gray-800/80 px-1.5 py-0.5 rounded border border-gray-700">
                                                {entry.interface_index < 10 ? `0${entry.interface_index}` : entry.interface_index}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-bold text-indigo-300">
                                                {entry.name}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-mono text-gray-400">
                                                {entry.mac_address || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                {/* Oper Status */}
                                                {entry.oper_status.toLowerCase() === "up" ? (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                        <CheckCircle2 size={10} /> Up
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[9px] font-bold uppercase tracking-wider rounded">
                                                        <XCircle size={10} /> Down
                                                    </span>
                                                )}
                                                
                                                {/* Admin Status Warning */}
                                                {entry.admin_status.toLowerCase() === "down" && (
                                                    <span className="text-gray-500 text-[9px] font-semibold flex items-center gap-0.5 uppercase" title="Administered Down">
                                                        <ShieldAlert size={10} /> Admin Down
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] font-mono font-bold text-gray-300">
                                                {entry.speed_mbps > 0 ? `${entry.speed_mbps} Mbps` : '—'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="text-[10px] font-medium text-gray-200">
                                                {new Date(entry.updated + "Z").toLocaleString()}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}